// Hooks de relatórios: relatório do período, PDF, links compartilháveis e resumo semanal.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { CreateShareLinkResponse, ReportDto, ReportScheduleDto, SharedReportResponse, ShareLinkDto } from '@impulso/shared/api/reports'
import { api, ApiError } from '../../lib/api'

export function useReport(companyId: string, days: number) {
  return useQuery({
    queryKey: ['reports', companyId, days],
    queryFn: () => api.get<ReportDto>(`/companies/${companyId}/reports?days=${days}`),
  })
}

export function useSharedReport(token: string) {
  return useQuery({
    queryKey: ['shared-report', token],
    queryFn: () => api.get<SharedReportResponse>(`/shared-reports/${encodeURIComponent(token)}`),
    retry: false,
  })
}

export function useShareLinks(companyId: string) {
  return useQuery({
    queryKey: ['share-links', companyId],
    queryFn: () => api.get<{ items: ShareLinkDto[] }>(`/companies/${companyId}/share-links`),
  })
}

export function useCreateShareLink(companyId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (days: number) => api.post<CreateShareLinkResponse>(`/companies/${companyId}/share-links`, { days }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['share-links', companyId] }),
  })
}

export function useRevokeShareLink(companyId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.del<void>(`/companies/${companyId}/share-links/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['share-links', companyId] }),
  })
}

export function useReportSchedule(companyId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['report-schedule', companyId],
    queryFn: () => api.get<ReportScheduleDto>(`/companies/${companyId}/report-schedules`),
    enabled,
  })
}

export function useSaveReportSchedule(companyId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { enabled: boolean; recipients: string[] }) => api.put<ReportScheduleDto>(`/companies/${companyId}/report-schedules`, body),
    onSuccess: (data) => qc.setQueryData(['report-schedule', companyId], data),
  })
}

/** Baixa o PDF (mesmo cookie de sessão). */
export async function downloadPdf(url: string, fallbackName: string): Promise<void> {
  const res = await fetch(`/api/v1${url}`, { credentials: 'include' })
  if (!res.ok) {
    let message = 'Não foi possível gerar o PDF. Tente de novo.'
    try { message = (await res.json()).error?.message ?? message } catch { /* corpo não é JSON */ }
    throw new ApiError(res.status, 'pdf', message)
  }
  const blob = await res.blob()
  const name = /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ?? fallbackName
  const href = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = href
  a.download = name
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(href), 1000)
}
